import { beneficiaryService } from "../../services/beneficiary.service";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
	BeneficiaryPayload,
	ConsentPayload,
	Phone,
} from "../../domain/beneficiary.interface";

const {
	findAllV2,
	findByIdV2,
	create,
	update,
	updateReconfirm,
	getConsentForms,
	patchConsentForm,
	getConsentFormUrl,
	findProgramsV2,
	findProvidersV2,
	findProviderSites,
	disenroll,
	reenroll,
	findByName,
	getCurrentWeekUdtStatus,
	findListVisits,
	getEnrollmentPeriods,
	findIncentiveHistory,
	putBeneficiaryInvite,
	updatePayerPlan,
} = beneficiaryService;

export const useGetBeneficiaries = ({
	providerSite = [],
	programId = "",
	providerId = "",
	userId = "",
	status = ["ACTIVE"],
	search = "",
	pageNumber,
	pageSize,
}: {
	providerSite?: string[];
	programId?: string;
	providerId?: string;
	userId?: string;
	status?: string[];
	search?: string;
	pageNumber: number;
	pageSize: number;
}) => {
	const beneficiaries = useQuery({
		queryKey: ["sims-beneficiaries"],
		queryFn: () =>
			findAllV2({
				providerSite,
				programId,
				providerId,
				userId,
				status,
				search,
				pageNumber,
				pageSize,
			}),
		staleTime: 1000 * 60 * 60,
		enabled: false,
	});

	return beneficiaries;
};

export const useGetBeneficiaryById = (
	beneficiaryId: string,
	enrollmentId?: string,
) => {
	return useQuery({
		queryKey: ["sims-beneficiary", beneficiaryId],
		queryFn: () => findByIdV2(beneficiaryId, enrollmentId),
		staleTime: 1000 * 60 * 60,
		enabled: !!beneficiaryId,
		retry: false,
	});
};

export const useCreateBeneficiary = () => {
	return useMutation({
		mutationFn: (data: BeneficiaryPayload) => create(data),
	});
};

export const useUpdateBeneficiary = ({
	beneficiaryId,
	enrollmentId,
}: {
	beneficiaryId: string;
	enrollmentId: string;
}) => {
	return useMutation({
		mutationFn: (data: Partial<BeneficiaryPayload>) =>
			update(data, beneficiaryId, enrollmentId),
	});
};

export const useUpdateReconfirmBeneficiary = () => {
	return useMutation({
		mutationFn: (data: {
			userId: string;
			beneficiaryId: string;
			enrollmentId: string;
		}) => updateReconfirm(data.userId, data.beneficiaryId, data.enrollmentId),
	});
};

export const useGetConsentForms = (beneficiaryId: string) => {
	return useQuery({
		queryKey: ["sims-beneficiaries-consent-forms"],
		queryFn: () => getConsentForms(beneficiaryId),
	});
};

export const usePatchConsentForm = (
	beneficiaryId: string,
	enrollmentId: string,
) => {
	return useMutation({
		mutationFn: (data: ConsentPayload) =>
			patchConsentForm(data, beneficiaryId, enrollmentId),
	});
};

/**
 * SIMS-6740: takes the ENROLLMENT, not the consent's `s3Url` — backend-2 resolves the object
 * itself rather than presigning a client-supplied URL.
 *
 * The query key now includes the enrollment id. Under the old static key every enrollment's
 * download shared one cache entry, so opening a second participant's consent could serve the
 * first one's URL from cache.
 */
export const useGetConsentFormUrl = (
	beneficiaryId: string,
	enrollmentId: string,
) => {
	return useQuery({
		queryKey: [
			"sims-beneficiaries-consent-form-url",
			beneficiaryId,
			enrollmentId,
		],
		queryFn: () => getConsentFormUrl(beneficiaryId, enrollmentId),
		enabled: !!beneficiaryId && !!enrollmentId,
	});
};

export const useGetProgramsByIds = (programIds: string[]) => {
	return useQuery({
		// SIMS-7598: key MUST include programIds. With a static key, callers that
		// pass different id-sets shared one 7-day-cached entry, so whichever fired
		// first won — and an empty array serializes to `?ids=` (backend returns
		// ALL programs), which then leaked into every consumer (e.g. the B2D
		// program dropdown showing all programs regardless of access). `enabled`
		// stops the empty-ids request entirely. Mirrors useGetParticipantPrograms.
		queryKey: ["sims-beneficiaries-programs", programIds],
		queryFn: () => findProgramsV2(programIds),
		staleTime: 1000 * 60 * 60 * 24 * 7,
		enabled: programIds.length > 0,
	});
};

export const useGetProviderSitesByOUId = ({
	ouIds,
	queryKey,
}: {
	ouIds: string[];
	queryKey?: string;
}) => {
	return useQuery({
		queryKey: [queryKey || "sims-beneficiaries-provider-sites", ouIds],
		queryFn: () => findProviderSites(ouIds),
		staleTime: 5 * 1000,
		enabled: ouIds.length > 0,
	});
};

export const useGetProvidersByProgramId = (
	programId: string[],
	providerSiteId: string[],
	includeAll?: boolean,
) => {
	return useQuery({
		queryKey: ["sims-beneficiaries-providers"],
		queryFn: () =>
			findProvidersV2(programId, providerSiteId, includeAll || false),
		staleTime: 1000 * 60 * 60 * 24 * 7,
		enabled:
			programId.filter(Boolean).length > 0 &&
			providerSiteId.filter(Boolean).length > 0,
	});
};

export const useDisenrollBeneficiary = ({
	beneficiaryId,
	enrollmentId,
}: {
	beneficiaryId: string;
	enrollmentId: string;
}) => {
	return useMutation({
		mutationFn: (data: { reasons: string; notes: string; userId: string }) =>
			disenroll(data, beneficiaryId, enrollmentId),
	});
};

export const useReenrollBeneficiary = ({
	beneficiaryId,
	enrollmentId,
}: {
	beneficiaryId: string;
	enrollmentId: string;
}) => {
	return useMutation({
		mutationFn: (data: { userId: string }) =>
			reenroll(data, beneficiaryId, enrollmentId),
	});
};

export const useGetSearchBeneficiary = (searchName: string, userId: string) => {
	return useQuery({
		queryKey: ["get-beneficiary-searched"],
		queryFn: () => findByName(searchName, userId),
	});
};

export const useGetCurrentWeekUdtStatus = (
	enrollmentId: string,
	behaviorId?: string,
	treatmentConfigName?: string,
) => {
	return useQuery({
		queryKey: [
			"get-current-week-udt-status",
			enrollmentId,
			behaviorId,
			treatmentConfigName,
		],
		queryFn: () =>
			getCurrentWeekUdtStatus(enrollmentId, behaviorId, treatmentConfigName),
		enabled: !!enrollmentId,
		retry: false,
		// Disable caching to ensure fresh data for each activity
		gcTime: 0,
		staleTime: 0,
	});
};

export const useGetListVisits = (enrollmentId: string, rangeWeeks: string) => {
	return useQuery({
		queryKey: ["get-list-visits", enrollmentId, rangeWeeks],
		queryFn: () =>
			findListVisits(enrollmentId, rangeWeeks).then((data) =>
				data?.sort((a, b) => Number(b.visit) - Number(a.visit)),
			),
		enabled: !!enrollmentId,
		retry: false,
	});
};

export const usePatchParticipant = (beneficiaryId: string) => {
	return useMutation({
		mutationFn: (data: { email?: string; phone?: Phone[]; userId: string }) =>
			beneficiaryService.patchBeneficiary(beneficiaryId, data),
	});
};

export const useGetBeneficiaryEnrollmentPeriods = (beneficiaryId: string) => {
	return useQuery({
		queryKey: ["get-beneficiary-enrollment-periods", beneficiaryId],
		queryFn: () => getEnrollmentPeriods(beneficiaryId),
	});
};

export const useGetListIncentives = ({
	enrollmentId,
	pageNumber,
	pageSize,
}: {
	enrollmentId: string;
	pageNumber: number;
	pageSize: number;
}) => {
	const query = useQuery({
		queryKey: [
			"get-benefiary-get-list-incentives",
			enrollmentId,
			pageNumber,
			pageSize,
		],
		queryFn: () => findIncentiveHistory(enrollmentId, pageNumber, pageSize),
		enabled: !!enrollmentId,
		retry: false,
	});

	return {
		incentives: query.data,
		isLoading: query.isLoading,
		refetchIncentives: query.refetch,
	};
};

export const usePutBeneficiaryInvite = (beneficiaryId: string) => {
	return useMutation({
		mutationFn: (invitedBy: string) =>
			putBeneficiaryInvite(beneficiaryId, invitedBy),
	});
};

export const useUpdateBeneficiaryPayerPlan = (beneficiaryId: string) => {
	return useMutation({
		mutationFn: (data: {
			payerId: string;
			oldMemberIdNumber: string;
			newMemberIdNumber: string;
			changedBy?: string;
		}) => updatePayerPlan(beneficiaryId, data),
	});
};

export * from "./useGetBeneficiaryPayerPlans";
